export interface HistoryMutation {
  type: string;
  payload: unknown;
  meta?: Record<string, unknown>;
}

export interface HistoryPatch {
  undo: HistoryMutation;
  redo: HistoryMutation;
}

export interface HistoryEntry {
  id: string;
  label: string;
  patches: HistoryPatch[];
  timestamp: number;
}

export type HistoryApplyFn = (
  mutation: HistoryMutation,
  direction: "undo" | "redo",
) => void;

export type HistoryListener = (state: {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
}) => void;

class HistoryManagerClass {
  private stack: HistoryEntry[] = [];
  private cursor = -1;
  private maxSize = 100;
  private applyFn: HistoryApplyFn | null = null;
  private listeners = new Set<HistoryListener>();

  bind(fn: HistoryApplyFn): void {
    this.applyFn = fn;
  }

  subscribe(listener: HistoryListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const s = this.snapshot();
    this.listeners.forEach((l) => l(s));
  }

  private snapshot() {
    return {
      canUndo: this.canUndo,
      canRedo: this.canRedo,
      undoLabel: this.undoLabel,
      redoLabel: this.redoLabel,
    };
  }

push(entry: HistoryEntry): void {
    this.stack = this.stack.slice(0, this.cursor + 1);

    this.stack.push(entry);

    if (this.stack.length > this.maxSize) {
        this.stack.shift();
    }

    this.cursor = this.stack.length - 1;

    this.notify();
}

  undo(): boolean {
    if (!this.canUndo || !this.applyFn) return false;
    const entry = this.stack[this.cursor]!;
    [...entry.patches].reverse().forEach((p) => this.applyFn!(p.undo, "undo"));
    this.cursor--;
    this.notify();
    return true;
  }

  redo(): boolean {
    if (!this.canRedo || !this.applyFn) return false;
    this.cursor++;
    const entry = this.stack[this.cursor]!;
    entry.patches.forEach((p) => this.applyFn!(p.redo, "redo"));
    this.notify();
    return true;
  }

  get canUndo(): boolean {
    return this.cursor >= 0;
  }

  get canRedo(): boolean {
    return this.cursor < this.stack.length - 1;
  }

  get undoLabel(): string | null {
    return this.cursor >= 0 ? this.stack[this.cursor]?.label ?? null : null;
  }

  get redoLabel(): string | null {
    const next = this.cursor + 1;
    return next < this.stack.length ? this.stack[next]?.label ?? null : null;
  }

  clear(): void {
    this.stack = [];
    this.cursor = -1;
    this.notify();
  }
}

export const HistoryManager = new HistoryManagerClass();